import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { FlightsList } from "@/features/flights/components/flight-list/FlightsList";
import { SelectedDepartureFlightDrawer } from "@/features/flights/components/flight-list/SelectedDepartureFlightDrawer";
import { useFlightFiltersContext } from "@/features/flights/contexts/FlightFiltersContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useReturnFlightSearch } from "@/features/flights/hooks/flightSearch/useReturnFlightSearch";
import {
  useFlightQuickSearchTokens,
  useFlightSearchTokens,
} from "@/features/flights/hooks/useFlightSearchTokens";
import type {
  FlightType,
  MappedFlightOffer,
} from "@/features/flights/types/types";

export const Route = createFileRoute(
  "/_authenticated/flights/_offers/return/list",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { history } = useRouter();
  const intl = useIntl();

  const isFlightBrandedFaresOn = useFeatureIsOn("flight-branded-fares");

  const {
    handleReturnFlightSelection,
    selectedDepartureFlightId,
    selectedFlightSearchSource,
  } = useFlightSelectionPayloadContext();
  const flightSearch = useFlightSearchTokens({
    enabled: selectedFlightSearchSource === "full",
  });
  const quickTokensQuery = useFlightQuickSearchTokens();
  const { filters } = useFlightFiltersContext();
  const searchQuery = useReturnFlightSearch(
    selectedFlightSearchSource === "quick"
      ? quickTokensQuery
      : flightSearch.query,
    filters,
    selectedDepartureFlightId,
  );

  const handleFlightSelect = (offer: MappedFlightOffer, flight: FlightType) => {
    handleReturnFlightSelection(offer, flight.key);

    if (isFlightBrandedFaresOn && offer.upsellSupport) {
      navigate({
        to: "/flights/departure/fare",
      });
    } else {
      navigate({
        to: "/flights/offer/$id/details",
        params: { id: offer.index },
      });
    }
  };

  return (
    <main className="no-footer main h-screen pb-[calc(var(--spacing)*2+var(--departure-flight-drawer-height,0))]">
      <FlightsList
        onFlightSelect={handleFlightSelect}
        direction="return"
        searchQuery={{ query: searchQuery, type: selectedFlightSearchSource }}
        title={
          <div>
            {intl.formatMessage({
              defaultMessage: "Choose return flight",
              description: "Page title when selecting the return flight",
            })}
          </div>
        }
        // when user triggers new search he has to select departure flight again so we need to navigate back to departure flights list
        onAfterSearchSubmit={() => history.back()}
      />
      <SelectedDepartureFlightDrawer />
      <WhatsAppButton
        bottomPosition={
          "calc(var(--departure-flight-drawer-height) + calc(var(--spacing)*4))"
        }
      />
    </main>
  );
}
